// Italian Fiscal Code (Codice Fiscale) validator.
// Validates format (16 alphanumeric chars) AND the checksum control character.
// Reference: D.M. 12/03/1974, Ministero delle Finanze.

const ODD_MAP = {
  A: 1,  B: 0,  C: 5,  D: 7,  E: 9,  F: 13, G: 15, H: 17, I: 19, J: 21,
  K: 2,  L: 4,  M: 18, N: 20, O: 11, P: 3,  Q: 6,  R: 8,  S: 12, T: 14,
  U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
  0: 1,  1: 0,  2: 5,  3: 7,  4: 9,  5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
};

const EVEN_MAP = {
  A: 0,  B: 1,  C: 2,  D: 3,  E: 4,  F: 5,  G: 6,  H: 7,  I: 8,  J: 9,
  K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18, T: 19,
  U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25,
  0: 0,  1: 1,  2: 2,  3: 3,  4: 4,  5: 5,  6: 6,  7: 7,  8: 8,  9: 9,
};

const CHECK_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const CF_REGEX = /^[A-Z]{6}\d{2}[A-EHLMPRST]\d{2}[A-Z]\d{3}[A-Z]$/;

// Month letter → month number (1-12)
const MONTH_MAP = {
  A: 1,  B: 2,  C: 3,  D: 4,  E: 5,  H: 6,
  L: 7,  M: 8,  P: 9,  R: 10, S: 11, T: 12,
};

/**
 * Derive birth date (ISO YYYY-MM-DD) from a valid Italian Codice Fiscale.
 * Returns null if CF is invalid or not derivable.
 * Notes:
 *  - For women, the day is stored as day+40
 *  - Year inference: 2-digit year > (currentYear - 2000) -> 1900s, else 2000s
 *
 * @param {string} cf
 * @returns {string|null} ISO date string YYYY-MM-DD or null
 */
export const deriveBirthDateFromCF = (cf) => {
  if (!cf) return null;
  const code = String(cf).trim().toUpperCase().replace(/\s/g, "");
  if (!CF_REGEX.test(code)) return null;

  const yy = parseInt(code.substring(6, 8), 10);
  const monthLetter = code[8];
  let dd = parseInt(code.substring(9, 11), 10);
  if (dd > 40) dd -= 40; // women

  const month = MONTH_MAP[monthLetter];
  if (!month || isNaN(yy) || isNaN(dd)) return null;

  // Year inference: if yy <= (currentYearLast2 + 5) assume 2000s, else 1900s
  const currentYear = new Date().getFullYear();
  const currentYY = currentYear % 100;
  const year = yy <= (currentYY + 5) ? 2000 + yy : 1900 + yy;

  // Validate day-of-month
  const date = new Date(Date.UTC(year, month - 1, dd));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== dd) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
};

/**
 * Derive gender from CF: 'M' or 'F' or null.
 */
export const deriveGenderFromCF = (cf) => {
  if (!cf) return null;
  const code = String(cf).trim().toUpperCase().replace(/\s/g, "");
  if (!CF_REGEX.test(code)) return null;
  const dd = parseInt(code.substring(9, 11), 10);
  if (isNaN(dd)) return null;
  return dd > 40 ? "F" : "M";
};

/**
 * Validate an Italian Codice Fiscale.
 * @param {string} cf - Raw Codice Fiscale input (case-insensitive)
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateCodiceFiscale = (cf) => {
  if (!cf) return { valid: false, error: "Codice Fiscale obbligatorio" };

  const code = String(cf).trim().toUpperCase().replace(/\s/g, "");

  if (code.length !== 16) {
    return { valid: false, error: "Il Codice Fiscale deve essere di 16 caratteri" };
  }

  if (!CF_REGEX.test(code)) {
    return { valid: false, error: "Formato Codice Fiscale non valido" };
  }

  // Compute checksum on the first 15 characters
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const ch = code[i];
    // Position is 1-indexed: i=0 -> pos 1 (odd), i=1 -> pos 2 (even)
    const isOdd = (i + 1) % 2 === 1;
    const val = isOdd ? ODD_MAP[ch] : EVEN_MAP[ch];
    if (val === undefined) {
      return { valid: false, error: "Codice Fiscale contiene caratteri non validi" };
    }
    sum += val;
  }

  const expectedCheck = CHECK_LETTERS[sum % 26];
  if (expectedCheck !== code[15]) {
    return { valid: false, error: "Carattere di controllo non valido (Codice Fiscale errato)" };
  }

  return { valid: true };
};

/**
 * Validate an Italian VAT number (P.IVA): 11 digits with checksum.
 * Optional country prefix (e.g., "IT01234567890").
 * @param {string} vat
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateVatNumber = (vat) => {
  if (!vat) return { valid: true }; // VAT is optional

  const raw = String(vat).trim().toUpperCase().replace(/\s/g, "");
  // Strip optional IT prefix
  const code = raw.startsWith("IT") ? raw.slice(2) : raw;

  if (code.length !== 11 || !/^\d{11}$/.test(code)) {
    return { valid: false, error: "La Partita IVA italiana deve avere 11 cifre" };
  }

  // Luhn-like checksum for Italian VAT
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let n = parseInt(code[i], 10);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(code[10], 10)) {
    return { valid: false, error: "Partita IVA non valida (checksum errato)" };
  }
  return { valid: true };
};
